import AccountCircle from "@mui/icons-material/AccountCircle";
import Brightness4 from "@mui/icons-material/Brightness4";
import Brightness7 from "@mui/icons-material/Brightness7";
import Label from "@mui/icons-material/Label";
import Logout from "@mui/icons-material/Logout";
import Menu from "@mui/icons-material/Menu";
import QrCode2 from "@mui/icons-material/QrCode2";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import WidthFull from "@mui/icons-material/WidthFull";
import WidthNormal from "@mui/icons-material/WidthNormal";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  Menu as MuiMenu,
  Stack,
  StackProps,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  LEGAL_MENU_TARGETS,
  PRODUCT_POSTHOG_EVENTS,
} from "encamp-shared/src/instrumentation/events";
import { getFullName, initials } from "encamp-shared/src/utils/name";
import { Permission } from "generated-graphql/graphql";
import { useHomeRoute } from "hooks/useHomeRoute";
import { useScreenWidth } from "hooks/useScreenWidth";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScoutTopNavTrigger } from "routes/Customer/Scout/PopoverScout";
import { EditUserDialog } from "routes/Customer/Users/EditUserDialog";
import { useCanSelfAssignFacilities } from "routes/Customer/Users/useCanLinkFacilities";
import {
  USER_PROFILE_MENU_ITEM_ANCHOR_ID,
  useFacilitySelfAssignmentTour,
} from "routes/Customer/Users/useFacilitySelfAssignmentTour";
import { performOakSignOut } from "utils/performOakSignOut";

import { useCurrentUser } from "../../hooks/useCurrentUser";
import { capturePosthog } from "../../instrumentation/posthog";
import { useSearchPills } from "../../providers/searchPills";
import { brandColors } from "../../util";
import { useAlerts } from "../Alerts/AlertProvider";
import { AppBar } from "../AppBar";
import { useRegisterCommands, type Command } from "../CommandPaletteRegistry";
import { EmulatePermissionsDialog } from "../EmulatePermissionsDialog";
import { EnvBar } from "../EnvBar";
import NavBreadcrumbs from "../NavBreadcrumbs";
import { NotificationBell } from "../Notifications/NotificationBell";
import { NotificationBellBoundary } from "../Notifications/NotificationBellBoundary";
import { useNotificationsEnabled } from "../Notifications/useNotificationsEnabled";
import { SpotlightTour } from "../ProductAnnouncement/SpotlightTour";
import { TenantPickerDialog } from "../TenantPickerDialog";

const NAV_ACTION_SIZE = 40;

function stringAvatar({ name }: { name: string }) {
  return {
    sx: {
      width: NAV_ACTION_SIZE,
      height: NAV_ACTION_SIZE,
      bgcolor: brandColors["encamp-blue"][100],
      color: brandColors["encamp-blue"][600],
      fontSize: "1rem",
      fontWeight: 700,
    },
    children: initials(name.split(" ")[0], name.split(" ")[1]),
  };
}

export function TopNavBarV2({
  handleThemeToggle,
  onMenuClick,
}: {
  handleThemeToggle: () => void;
  onMenuClick: () => void;
}) {
  const theme = useTheme();
  const notificationsEnabled = useNotificationsEnabled();
  const { isStaff, isOriginalUserStaff, isEmulating, user, isPartner } = useCurrentUser();
  const {
    enabled: searchPillsEnabled,
    setEnabled: setSearchPillsEnabled,
    canToggle: canToggleSearchPills,
  } = useSearchPills();
  const { tenant, loading: loadingTenant } = useTenant();
  const isHome = useHomeRoute();

  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  // Register command to open the tenant picker (staff/partners only)
  const tenantPickerCommand: Command[] = useMemo(() => {
    if (!(isOriginalUserStaff || isPartner)) return [];
    return [
      {
        id: "open-tenant-picker",
        label: "Open tenant picker",
        description: "Change organization",
        type: "open",
        keywords: ["tenant", "picker", "organization", "switch", "change"],
        signature: `open-tenant-picker:${isOriginalUserStaff || isPartner}`,
        run: () => setTenantPickerOpen(true),
      },
    ];
  }, [isOriginalUserStaff, isPartner]);
  useRegisterCommands(tenantPickerCommand, [tenantPickerCommand], "tenant-picker");
  const [emulateDialogOpen, setEmulateDialogOpen] = useState(false);

  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [hasDismissedEditUserDialog, setHasDismissedEditUserDialog] = useState(false);

  const themeMode = theme.palette.mode;
  /** True when the left nav is opened via the mobile overlay drawer pattern. */
  const isMobileDrawerMode = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const themeToggleTitle = `Switch to ${themeMode === "dark" ? "light" : "dark"} theme`;
  const { screenWidth, toggleScreenWidth } = useScreenWidth();
  const screenToggleTitle = `Switch to ${screenWidth === "full" ? "fixed" : "full"} width`;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const alerts = useAlerts();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const signOut = useCallback(async () => {
    await performOakSignOut({ redirectTo: "/" });
  }, []);

  const openTenantPicker = useCallback(() => {
    setTenantPickerOpen(true);
    setAnchorEl(null);
  }, []);

  const OrgName = useMemo(() => {
    const tenantName = loadingTenant ? "Loading..." : (tenant?.name ?? "No current organization");
    return (
      <>
        <span>{tenantName}</span>{" "}
        {(isStaff || isPartner) && (
          <span>
            (
            <span
              style={{ cursor: "pointer", color: theme.palette.primary.main }}
              onClick={openTenantPicker}
            >
              change org
            </span>
            )
          </span>
        )}
      </>
    );
  }, [
    isStaff,
    tenant?.name,
    theme.palette.primary.main,
    isPartner,
    loadingTenant,
    openTenantPicker,
  ]);

  const open = Boolean(anchorEl);

  const UserInfo = () => (
    <Stack direction="column" alignItems={isLargeScreen ? "flex-end" : "flex-start"}>
      <Typography
        variant="body2"
        component="div"
        sx={{
          mt: theme.spacing(0.5),
          fontWeight: "bold",
        }}
      >
        {getFullName(user?.person)}
        {isEmulating && ` (as emulated user)`}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: "right" }}>
        {OrgName}
      </Typography>
    </Stack>
  );

  const userTenant = user?.UserTenant.find((userTenant) => userTenant.tenantId === tenant?.id);
  const permissions = userTenant?.role?.permissions ?? [];
  const accessesAllFacilities =
    isStaff ||
    permissions.includes(Permission.ReadAllFacility) ||
    permissions.includes(Permission.WriteAllFacility);
  const userHasNoFacilities = user?.facilities.length === 0 && !accessesAllFacilities;

  const canSelfAssignFacilities = useCanSelfAssignFacilities();
  const autoOpenEditUserDialog = useMemo(() => {
    return canSelfAssignFacilities && userHasNoFacilities;
  }, [canSelfAssignFacilities, userHasNoFacilities]);

  const openProfileMenu = useCallback(() => setAnchorEl(profileButtonRef.current), []);
  const openProfile = useCallback(() => {
    setEditUserDialogOpen(true);
    setAnchorEl(null);
  }, []);
  // A user who has already found their profile this session needs no tour to it.
  const facilityTour = useFacilitySelfAssignmentTour({
    openProfileMenu,
    openProfile,
    skip: !canSelfAssignFacilities || accessesAllFacilities || hasDismissedEditUserDialog,
  });

  // The tour ends in the profile, so once it has run it stands in for the auto-open.
  const [facilityTourRan, setFacilityTourRan] = useState(false);
  const completeFacilityTour = () => {
    setFacilityTourRan(true);
    facilityTour.completeTour();
  };
  const dismissFacilityTour = () => {
    setFacilityTourRan(true);
    facilityTour.dismissTour();
  };

  const shouldAutoOpenEditUserDialog =
    autoOpenEditUserDialog &&
    !hasDismissedEditUserDialog &&
    !editUserDialogOpen &&
    !facilityTour.isPending &&
    !facilityTourRan;
  useEffect(() => {
    if (shouldAutoOpenEditUserDialog) {
      // open the user edit dialog only once automatically
      setEditUserDialogOpen(true);
    }
  }, [shouldAutoOpenEditUserDialog]);

  // Removed color cycling and env label mode; EnvBar derives color from VITE_ENV
  const [showEnvBar, setShowEnvBar] = useState<boolean>(() => {
    return (sessionStorage.getItem("oak-show-env-bar") ?? "true") === "true";
  });
  const envBarToggleTitle = showEnvBar ? "Hide Environment Bar" : "Show Environment Bar";

  return (
    <>
      <AppBar>
        <Toolbar sx={{ height: "100%" }}>
          {showEnvBar && isOriginalUserStaff && <EnvBar />}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={isHome && !isMobileDrawerMode ? "flex-start" : "center"}
            spacing={2}
            sx={{ width: "100%", display: "flex" }}
          >
            {isHome && isLargeScreen && (
              <HomeHeader paddingTop={theme.spacing(4)} paddingBottom={theme.spacing(2)} />
            )}
            {isMobileDrawerMode && (
              <IconButton aria-label="Open navigation" onClick={onMenuClick}>
                <Menu sx={{ width: "24px", height: "24px" }} />
              </IconButton>
            )}
            <NavBreadcrumbs />
            <Stack direction="row" alignItems="center" spacing={1} paddingY={theme.spacing(2)}>
              {isLargeScreen && <UserInfo />}
              <ScoutTopNavTrigger />
              {notificationsEnabled ? (
                <NotificationBellBoundary>
                  <NotificationBell />
                </NotificationBellBoundary>
              ) : null}
              <IconButton
                ref={profileButtonRef}
                onClick={handleClick}
                size="small"
                sx={{
                  width: NAV_ACTION_SIZE,
                  height: NAV_ACTION_SIZE,
                  padding: 0,
                }}
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                <Avatar
                  {...stringAvatar({
                    name: getFullName(user?.person),
                  })}
                />
              </IconButton>
            </Stack>
          </Stack>
        </Toolbar>

        {/* Mobile only - display home header below the toolbar */}
        {isHome && !isLargeScreen && (
          <HomeHeader paddingX={theme.spacing(3)} paddingBottom={theme.spacing(2)} />
        )}
      </AppBar>
      <MuiMenu id="simple-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        {!isLargeScreen && [
          <Box key="user-info" component="li" sx={{ px: 2, py: 1.5, listStyle: "none" }}>
            <UserInfo />
          </Box>,
          <Divider key="user-info-divider" />,
        ]}
        <MenuItem
          component="a"
          href="https://encamp.com/legal/security-statement/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            capturePosthog(PRODUCT_POSTHOG_EVENTS.ACCESSED_LEGAL_MENU, {
              target: LEGAL_MENU_TARGETS.SECURITY,
            })
          }
        >
          Security
        </MenuItem>
        <MenuItem
          component="a"
          href="https://encamp.com/legal/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            capturePosthog(PRODUCT_POSTHOG_EVENTS.ACCESSED_LEGAL_MENU, {
              target: LEGAL_MENU_TARGETS.PRIVACY,
            })
          }
        >
          Privacy
        </MenuItem>
        <MenuItem
          component="a"
          href="https://encamp.com/legal/terms-of-service/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            capturePosthog(PRODUCT_POSTHOG_EVENTS.ACCESSED_LEGAL_MENU, {
              target: LEGAL_MENU_TARGETS.TERMS_OF_SERVICE,
            })
          }
        >
          Terms of Service
        </MenuItem>
        {isOriginalUserStaff && [
          <Divider key="divider-1" />,
          <MenuItem
            key="toggle-theme"
            onClick={() => {
              handleThemeToggle();
              handleClose();
            }}
          >
            <ListItemIcon>{themeMode === "dark" ? <Brightness7 /> : <Brightness4 />}</ListItemIcon>
            {themeToggleTitle}
          </MenuItem>,

          canToggleSearchPills ? (
            <MenuItem
              key="toggle-search-pills"
              onClick={() => {
                setSearchPillsEnabled(!searchPillsEnabled);
                handleClose();
              }}
            >
              <ListItemIcon>
                <Label />
              </ListItemIcon>
              {searchPillsEnabled ? "Disable search pills" : "Enable search pills"}

              <Chip
                label="Preview"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ ml: 1.5 }}
              />
            </MenuItem>
          ) : null,

          <MenuItem
            key="toggle-env-bar-visibility"
            onClick={() => {
              setShowEnvBar((prev) => {
                const next = !prev;
                sessionStorage.setItem("oak-show-env-bar", String(next));
                return next;
              });
              handleClose();
            }}
          >
            <ListItemIcon>{showEnvBar ? <VisibilityOff /> : <Visibility />}</ListItemIcon>
            {envBarToggleTitle}
          </MenuItem>,

          <MenuItem
            key="toggle-width"
            onClick={() => {
              toggleScreenWidth();
              handleClose();
            }}
          >
            <ListItemIcon>{screenWidth === "full" ? <WidthNormal /> : <WidthFull />}</ListItemIcon>
            {screenToggleTitle}
          </MenuItem>,
          <MenuItem
            key="emulate-permissions"
            onClick={() => {
              setEmulateDialogOpen(true);
              handleClose();
            }}
          >
            <ListItemIcon>
              <Menu />
            </ListItemIcon>
            Emulate Permissions
          </MenuItem>,
        ]}
        <Divider />
        <MenuItem id={USER_PROFILE_MENU_ITEM_ANCHOR_ID} onClick={openProfile}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          User Profile
        </MenuItem>
        {import.meta.env.VITE_ENV !== "release" && (
          <MenuItem
            onClick={() => {
              const currentUrl = window.location.href;
              const returnUrl = window.location.pathname + window.location.search;
              navigate(
                `/qr-code?url=${encodeURIComponent(
                  currentUrl
                )}&return=${encodeURIComponent(returnUrl)}`
              );
              handleClose();
            }}
          >
            <ListItemIcon>
              <QrCode2 fontSize="small" />
            </ListItemIcon>
            Generate QR Code
          </MenuItem>
        )}
        <MenuItem onClick={signOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </MuiMenu>
      <TenantPickerDialog
        open={tenantPickerOpen}
        onClose={() => {
          setTenantPickerOpen(false);
        }}
        onTenantSelected={(tenant) => {
          navigate(`/o/${tenant.id}/facilities`);
          alerts.success("Successfully changed organization");
          setTenantPickerOpen(false);
        }}
      />
      <EmulatePermissionsDialog
        open={emulateDialogOpen}
        tenant={tenant}
        onClose={() => setEmulateDialogOpen(false)}
      />
      {editUserDialogOpen && (
        <EditUserDialog
          tenantId={tenant?.id ?? ""}
          initialUserId={user?.id}
          open={editUserDialogOpen}
          onClose={() => {
            setEditUserDialogOpen(false);
            setHasDismissedEditUserDialog(true);
          }}
          introText={
            autoOpenEditUserDialog
              ? "Click the Link Facilities button to add facilities to your user."
              : undefined
          }
        />
      )}
      {facilityTour.phase === "tour" && (
        <SpotlightTour
          steps={facilityTour.steps}
          onComplete={completeFacilityTour}
          onDismiss={dismissFacilityTour}
          onStepViewed={facilityTour.viewTourStep}
        />
      )}
    </>
  );
}

const HomeHeader = (props: StackProps) => {
  return (
    <Stack spacing={1} {...props}>
      <Typography variant="h5">Welcome back to Encamp ⛺️</Typography>
      <Typography variant="subtitle1" color="text.secondary">
        Manage your environmental compliance
      </Typography>
    </Stack>
  );
};
